import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";

import type {
    ContextMenuConfig,
    ContextMenuPlacement,
    ContextMenuRootValue,
} from "../types/contextMenu.types";

const ContextMenuContext = createContext<ContextMenuRootValue | null>(null);

type ContextMenuRootProps = {
    children: ReactNode;
};

export function ContextMenuRoot({ children }: ContextMenuRootProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<ContextMenuPlacement | null>(null);
    const [menu, setMenu] = useState<ContextMenuConfig<unknown> | null>(null);
    const [context, setContext] = useState<unknown | null>(null);

    const openMenu = useCallback(
        <TContext,>({
            position,
            menu,
            context,
        }: {
            position: ContextMenuPlacement;
            menu: ContextMenuConfig<TContext>;
            context: TContext;
        }) => {
            setPosition(position);
            setMenu(menu as ContextMenuConfig<unknown>);
            setContext(context);
            setIsOpen(true);
        },
        [],
    );

    const closeMenu = useCallback(() => {
        setIsOpen(false);
        setPosition(null);
        setMenu(null);
        setContext(null);
    }, []);

    const value = useMemo<ContextMenuRootValue>(
        () => ({
            isOpen,
            position,
            menu,
            context,
            openMenu,
            closeMenu,
        }),
        [isOpen, position, menu, context, openMenu, closeMenu],
    );

    return (
        <ContextMenuContext.Provider value={value}>
            {children}
        </ContextMenuContext.Provider>
    );
}

export function useContextMenuRoot<TContext>() {
    const value = useContext(ContextMenuContext);

    if (!value) {
        throw new Error("useContextMenuRoot must be used inside ContextMenuRoot.");
    }

    return value as ContextMenuRootValue<TContext>;
}